import Papa from 'papaparse'
import * as XLSX from 'xlsx'

// Turns an arbitrary inventory export into a recovery analysis. Column names
// vary wildly between brands and 3PLs, so every field is matched by intent
// rather than an exact header.

export type AnalysisLine = {
  sku: string
  name: string
  brand: string
  category: string
  size: string
  color: string
  units: number
  msrp: number
  image: string
}

export type Breakdown = { label: string; units: number; value: number; share: number }

export type UploadAnalysis = {
  fileName: string
  headers: string[]
  mapping: Partial<Record<keyof AnalysisLine, string>>
  skippedRows: number
  rowCount: number
  lines: AnalysisLine[]
  totalUnits: number
  totalMsrp: number
  weightedMsrp: number
  recoveryRate: number
  expectedRecovery: number
  conservativeRecovery: number
  upsideRecovery: number
  recoveryPerUnit: number
  marketingAllocation: number
  confidence: number
  categories: Breakdown[]
  sizes: Breakdown[]
  priceBands: Breakdown[]
  topLines: AnalysisLine[]
  gaps: { label: string; count: number; detail: string }[]
  mappedColumns: { field: string; column: string }[]
  missingFields: string[]
}

// Ordered best-first. Shopify's own column names come first so a raw product
// export maps correctly, and columns that merely look numeric (inventory
// policy, tracker, product-level totals repeated on every variant row) are
// explicitly rejected rather than summed.
const fieldPatterns: { field: keyof AnalysisLine; patterns: RegExp[]; reject?: RegExp[] }[] = [
  {
    field: 'sku',
    patterns: [/^variant sku$/i, /^sku$/i, /^variant barcode$/i, /^barcode$/i, /^upc$/i, /sku/i, /item ?number/i],
  },
  { field: 'name', patterns: [/^title$/i, /^product title$/i, /product name/i, /^name$/i, /style ?name/i] },
  { field: 'brand', patterns: [/^vendor$/i, /^brand$/i, /vendor/i] },
  {
    field: 'category',
    patterns: [/^product category$/i, /^type$/i, /^product type$/i, /category/i, /department/i, /^class$/i],
  },
  { field: 'size', patterns: [/^option1 value$/i, /^size$/i, /variant size/i, /option\d value/i] },
  { field: 'color', patterns: [/^option2 value$/i, /colou?r/i] },
  {
    field: 'units',
    patterns: [
      /^variant inventory qty$/i,
      /^on hand$/i,
      /^available$/i,
      /^net received$/i,
      /^quantity$/i,
      /^qty$/i,
      /^units$/i,
      /inventory (qty|quantity|available)/i,
      /quantity/i,
    ],
    // Product-level totals repeat on every variant row, so summing them
    // multiplies the true unit count. Policy and tracker are not numbers.
    reject: [/policy/i, /tracker/i, /^total /i, /committed/i, /incoming/i, /unavailable/i, /location/i],
  },
  {
    field: 'msrp',
    patterns: [/^variant compare at price$/i, /^compare at price$/i, /msrp/i, /retail price/i, /original price/i, /list price/i, /^variant price$/i, /^price$/i],
    reject: [/cost/i, /unit cost/i],
  },
  { field: 'image', patterns: [/^image src$/i, /^variant image$/i, /image ?url/i, /^image$/i] },
]

export type ColumnField = keyof AnalysisLine
export type ColumnMapping = Partial<Record<ColumnField, string>>

export type ParsedFile = {
  fileName: string
  rows: Record<string, string>[]
  headers: string[]
  mapping: ColumnMapping
}

// Baseline recovery rate per condition, before the pricing limit is applied.
const conditionRates: Record<string, number> = {
  'New with tags': 0.52,
  'New without tags / Open box': 0.45,
  'Imperfect / B-grade': 0.39,
  Damaged: 0.24,
  Used: 0.3,
}

function toNumber(value: string) {
  const parsed = Number(String(value ?? '').replace(/[^0-9.-]/g, ''))
  return Number.isFinite(parsed) ? parsed : 0
}

// Score every header against the ordered patterns and take the best match, so
// "Variant Inventory Qty" always beats a vaguer "quantity"-ish column.
function matchColumns(headers: string[]): ColumnMapping {
  const mapping: ColumnMapping = {}
  for (const { field, patterns, reject } of fieldPatterns) {
    let best: { header: string; rank: number } | null = null
    for (const header of headers) {
      const name = header.trim()
      if (reject?.some((pattern) => pattern.test(name))) continue
      const rank = patterns.findIndex((pattern) => pattern.test(name))
      if (rank === -1) continue
      if (!best || rank < best.rank) best = { header, rank }
    }
    if (best) mapping[field] = best.header
  }
  return mapping
}

function summarize(groups: Map<string, { units: number; value: number }>, totalUnits: number): Breakdown[] {
  return Array.from(groups.entries())
    .map(([label, group]) => ({
      label,
      units: group.units,
      value: group.value,
      share: totalUnits > 0 ? (group.units / totalUnits) * 100 : 0,
    }))
    .sort((a, b) => b.units - a.units)
    .slice(0, 8)
}

// Excel files are binary, so they have to be decoded with SheetJS rather than
// read as text — otherwise every column comes back unmapped.
async function readRows(file: File): Promise<{ rows: Record<string, string>[]; headers: string[] }> {
  const isExcel = /\.(xlsx|xls)$/i.test(file.name)

  if (isExcel) {
    const buffer = await file.arrayBuffer()
    const workbook = XLSX.read(buffer, { type: 'array' })
    const sheet = workbook.Sheets[workbook.SheetNames[0]]
    if (!sheet) return { rows: [], headers: [] }
    const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '', raw: false })
    const normalized = rows.map((row) =>
      Object.fromEntries(Object.entries(row).map(([key, value]) => [key, String(value ?? '')])),
    )
    const headers = Object.keys(normalized[0] ?? {})
    return { rows: normalized, headers }
  }

  const text = await file.text()
  const parsed = Papa.parse<Record<string, string>>(text, { header: true, skipEmptyLines: true })
  return { rows: parsed.data, headers: parsed.meta.fields ?? [] }
}

export async function parseInventoryFile(file: File): Promise<ParsedFile> {
  const { rows: rawRows, headers } = await readRows(file)
  const rows = rawRows.filter((row) => Object.values(row).some((value) => String(value ?? '').trim() !== ''))
  return { fileName: file.name, rows, headers, mapping: matchColumns(headers) }
}

export function buildAnalysis(
  parsed: ParsedFile,
  options: { condition: string; indexPrice: number },
  overrides: ColumnMapping = {},
): UploadAnalysis {
  const { fileName, headers } = parsed
  const mapping: ColumnMapping = { ...parsed.mapping, ...overrides }

  const read = (row: Record<string, string>, field: ColumnField) => {
    const column = mapping[field]
    return column ? String(row[column] ?? '').trim() : ''
  }

  // Shopify repeats a product across rows to carry additional photos. Those
  // rows hold only a handle and an image URL — no title, SKU, price, or
  // quantity — so they are neither inventory nor a data-quality problem.
  const isProductRow = (row: Record<string, string>) =>
    read(row, 'sku') !== '' || read(row, 'name') !== '' || read(row, 'units') !== '' || read(row, 'msrp') !== ''

  const rows = parsed.rows.filter(isProductRow)
  const skippedRows = parsed.rows.length - rows.length

  // Shopify only writes title, vendor, type, and image on a product's first
  // row; later variant rows leave them blank. Carry the last seen value
  // forward so those variants are not reported as missing data.
  const carried = { name: '', brand: '', category: '', image: '' }
  const inherit = (row: Record<string, string>, field: 'name' | 'brand' | 'category' | 'image') => {
    const value = read(row, field)
    if (value) carried[field] = value
    return value || carried[field]
  }

  const lines: AnalysisLine[] = rows.map((row) => {
    // A blank quantity on a real product row means zero on hand, not one unit.
    const units = mapping.units ? Math.max(0, Math.round(toNumber(read(row, 'units')))) : 1
    return {
      sku: read(row, 'sku'),
      name: inherit(row, 'name') || read(row, 'sku') || 'Unnamed line',
      brand: inherit(row, 'brand'),
      category: inherit(row, 'category') || 'Uncategorized',
      size: read(row, 'size'),
      color: read(row, 'color'),
      units,
      msrp: toNumber(read(row, 'msrp')),
      image: inherit(row, 'image'),
    }
  })

  const totalUnits = lines.reduce((sum, line) => sum + line.units, 0)
  const totalMsrp = lines.reduce((sum, line) => sum + line.msrp * line.units, 0)
  const weightedMsrp = totalUnits > 0 ? totalMsrp / totalUnits : 0

  // The pricing limit caps how far product can be discounted, so a tighter
  // limit lifts the achievable recovery rate against MSRP.
  const base = conditionRates[options.condition] ?? 0.39
  const ceiling = 1 - options.indexPrice / 100
  const recoveryRate = Math.max(0.12, Math.min(base, ceiling + 0.08))
  const expectedRecovery = totalMsrp * recoveryRate
  const conservativeRecovery = expectedRecovery * 0.82
  const upsideRecovery = expectedRecovery * 1.18

  const group = (key: (line: AnalysisLine) => string) => {
    const map = new Map<string, { units: number; value: number }>()
    for (const line of lines) {
      const label = key(line) || 'Unspecified'
      const current = map.get(label) ?? { units: 0, value: 0 }
      map.set(label, { units: current.units + line.units, value: current.value + line.msrp * line.units })
    }
    return map
  }

  const bandFor = (msrp: number) =>
    msrp === 0 ? 'No MSRP' : msrp < 100 ? 'Under $100' : msrp < 150 ? '$100–149' : msrp < 200 ? '$150–199' : '$200+'

  const missingMsrp = lines.filter((line) => line.msrp === 0).length
  const missingSku = lines.filter((line) => !line.sku).length
  const missingImage = lines.filter((line) => !line.image).length
  const missingSize = lines.filter((line) => !line.size).length

  const coverage = (count: number) => (lines.length > 0 ? 1 - count / lines.length : 0)
  const confidence = Math.round(
    Math.min(
      94,
      Math.max(
        45,
        40 +
          coverage(missingMsrp) * 30 +
          coverage(missingSku) * 15 +
          coverage(missingImage) * 8 +
          coverage(missingSize) * 7,
      ),
    ),
  )

  const missingFields = fieldPatterns
    .filter(({ field }) => !mapping[field] && ['sku', 'brand', 'size', 'units', 'msrp', 'image'].includes(field))
    .map(({ field }) => field)

  return {
    fileName,
    headers,
    mapping,
    skippedRows,
    rowCount: rows.length,
    lines,
    totalUnits,
    totalMsrp,
    weightedMsrp,
    recoveryRate,
    expectedRecovery,
    conservativeRecovery,
    upsideRecovery,
    recoveryPerUnit: totalUnits > 0 ? expectedRecovery / totalUnits : 0,
    marketingAllocation: expectedRecovery * 0.1,
    confidence,
    categories: summarize(group((line) => line.category), totalUnits),
    sizes: summarize(group((line) => line.size), totalUnits),
    priceBands: summarize(group((line) => bandFor(line.msrp)), totalUnits),
    topLines: [...lines].sort((a, b) => b.msrp * b.units - a.msrp * a.units).slice(0, 8),
    gaps: [
      { label: 'Lines missing MSRP', count: missingMsrp, detail: 'Valued at zero until a price is supplied' },
      { label: 'Lines missing an identifier', count: missingSku, detail: 'Cannot be matched to sales without a SKU or barcode' },
      { label: 'Lines missing imagery', count: missingImage, detail: 'Need photography before they can be listed' },
      { label: 'Lines missing size', count: missingSize, detail: 'Weakens the size-curve read' },
    ].filter((gap) => gap.count > 0),
    mappedColumns: Object.entries(mapping).map(([field, column]) => ({ field, column: column as string })),
    missingFields,
  }
}
